"use client";

import {
  AlertTriangle,
  PauseCircle,
  PlayCircle,
  RefreshCcw,
  ShieldAlert,
} from "lucide-react";
import { useMutation, useQuery } from "convex/react";
import { makeFunctionReference } from "convex/server";
import { useState, type ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { QuerySkeleton } from "@/components/ui/skeleton";
import { useCurrentTenant } from "@/components/tenant/use-current-tenant";
import { getDemoAgentOpsSnapshot } from "@/lib/demo-data";

type LiveAgentOpsSnapshot = {
  runs: Array<{ runId: string; workflowName: string; status: string; createdAt: number; outputSummary?: string }>;
  events: Array<{ eventId: string; eventType: string; sourceSystem: string; status: string; createdAt: number }>;
  tasks: Array<{ taskId: string; title: string; status: string; createdAt: number }>;
  retries: unknown[];
  deadLetters: unknown[];
  counts: {
    runs: number;
    events: number;
    openTasks: number;
    needsApproval: number;
    heartbeatCreated: number;
    retries: number;
    deadLetters: number;
  };
};

const liveOpsQuery = makeFunctionReference<"query", { tenantId: string }, LiveAgentOpsSnapshot>(
  "agentRuntime:listAgentOpsSnapshot",
);
const listHeadlessAgents = makeFunctionReference<
  "query",
  { tenantId: string },
  Array<{ agentId: string; name: string; status: string; scopes: string[] }>
>("headlessAgents:listByTenant");
const pauseTenant = makeFunctionReference<
  "mutation",
  { tenantId: string; actorId: string; reason: string },
  void
>("agentOps:pauseTenant");
const resumeTenantReadAndDraft = makeFunctionReference<
  "mutation",
  { tenantId: string; actorId: string; reason: string },
  void
>("agentOps:resumeTenantReadAndDraft");
const revokeAgent = makeFunctionReference<
  "mutation",
  { tenantId: string; agentId: string; actorId: string; reason?: string },
  { agentId: string; status: "revoked" }
>("headlessAgents:revokeAgent");

type HeadlessAgentControlRow = {
  agentId: string;
  name: string;
  status: string;
  scopes: string[];
};

export function AgentOpsDashboard() {
  const tenant = useCurrentTenant();
  if (process.env.NEXT_PUBLIC_CONVEX_URL && tenant.status === "loading") {
    return <QuerySkeleton className="mx-auto mt-6 max-w-7xl" surface="consoleDashboard" />;
  }
  if (process.env.NEXT_PUBLIC_CONVEX_URL && tenant.status === "ready") {
    return <LiveAgentOpsDashboard tenantId={tenant.tenantId} tenantName={tenant.tenant.tenantName} />;
  }
  const snapshot = getDemoAgentOpsSnapshot();
  return <DemoAgentOpsPanel snapshot={snapshot} />;
}

function LiveAgentOpsDashboard({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const data = useQuery(liveOpsQuery, { tenantId });
  const agents = useQuery(listHeadlessAgents, { tenantId });
  const pause = useMutation(pauseTenant);
  const resume = useMutation(resumeTenantReadAndDraft);
  const revoke = useMutation(revokeAgent);
  const [controlStatus, setControlStatus] = useState("Ready");
  if (data === undefined) {
    return <QuerySkeleton className="mx-auto mt-6 max-w-7xl" surface="consoleDashboard" />;
  }
  const snapshot = {
    metrics: [
      { title: "Recent runs", value: String(data.counts.runs) },
      { title: "Queued events", value: String(data.counts.events) },
      { title: "Open tasks", value: String(data.counts.openTasks) },
      { title: "Dead letters", value: String(data.counts.deadLetters) },
    ],
    providerHealth: [
      { provider: "agent events", status: data.counts.deadLetters > 0 ? "degraded" : "healthy", latency: "live", staleSignals: data.counts.heartbeatCreated },
      { provider: "retry queue", status: data.counts.retries > 0 ? "degraded" : "healthy", latency: "live", staleSignals: data.counts.retries },
    ],
    tenantRows: [
      {
        tenant: tenantName,
        throughput: data.counts.runs,
        retries: data.counts.retries,
        deadLetters: data.counts.deadLetters,
        backlog: data.counts.needsApproval,
        status: "production_read_and_draft",
      },
    ],
    incidents: [
      ...data.deadLetters.slice(0, 2).map((_, index) => ({
        title: `Dead-letter requires review #${index + 1}`,
        severity: "critical" as const,
      })),
      ...data.tasks
        .filter((task) => task.status === "needs_approval")
        .slice(0, 2)
        .map((task) => ({ title: task.title, severity: "warn" as const })),
    ],
  };
  return (
    <AgentOpsPanel
      snapshot={snapshot}
      agents={agents ?? []}
      controlStatus={controlStatus}
      onPause={async () => {
        await pause({ tenantId, actorId: "current-user", reason: "Paused from Agent Ops console" });
        setControlStatus("Tenant paused");
      }}
      onResume={async () => {
        await resume({ tenantId, actorId: "current-user", reason: "Resumed from Agent Ops console" });
        setControlStatus("Tenant resumed to read and draft");
      }}
      onRevokeAgent={async (agentId) => {
        await revoke({ tenantId, agentId, actorId: "current-user", reason: "Revoked from Agent Ops console" });
        setControlStatus(`${agentId} revoked`);
      }}
    />
  );
}

function DemoAgentOpsPanel({ snapshot }: { snapshot: ReturnType<typeof getDemoAgentOpsSnapshot> }) {
  const [agents, setAgents] = useState<HeadlessAgentControlRow[]>([
    {
      agentId: "agent_dispatcher_default",
      name: "Haulbase Dispatcher Agent",
      status: "active",
      scopes: ["commands:submit", "loads:read", "routes:read"],
    },
  ]);
  const [controlStatus, setControlStatus] = useState("Ready");
  return (
    <AgentOpsPanel
      snapshot={snapshot}
      agents={agents}
      controlStatus={controlStatus}
      onPause={async () => setControlStatus("Tenant paused")}
      onResume={async () => setControlStatus("Tenant resumed to read and draft")}
      onRevokeAgent={async (agentId) => {
        setAgents((current) =>
          current.map((agent) => (agent.agentId === agentId ? { ...agent, status: "revoked" } : agent)),
        );
        setControlStatus(`${agentId} revoked`);
      }}
    />
  );
}

function AgentOpsPanel({
  snapshot,
  agents,
  controlStatus,
  onPause,
  onResume,
  onRevokeAgent,
}: {
  snapshot: ReturnType<typeof getDemoAgentOpsSnapshot>;
  agents: HeadlessAgentControlRow[];
  controlStatus: string;
  onPause: () => Promise<void>;
  onResume: () => Promise<void>;
  onRevokeAgent: (agentId: string) => Promise<void>;
}) {
  const [pendingControl, setPendingControl] = useState<string | null>(null);

  async function runControl(label: string, action: () => Promise<void>) {
    setPendingControl(label);
    try {
      await action();
    } finally {
      setPendingControl(null);
    }
  }

  return (
    <main className="mx-auto max-w-7xl space-y-6 px-5 py-6">
      <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Agent Operations</h1>
          <p className="text-sm text-muted-foreground">
            Managed freight agent throughput, provider health, incidents, retries, dead letters, and tenant controls.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary">
            <RefreshCcw size={16} />
            Refresh
          </Button>
          <Button
            variant="secondary"
            disabled={pendingControl !== null}
            onClick={() => runControl("pause", onPause)}
          >
            <PauseCircle size={16} />
            Pause tenant
          </Button>
          <Button disabled={pendingControl !== null} onClick={() => runControl("resume", onResume)}>
            <PlayCircle size={16} />
            Resume tenant
          </Button>
        </div>
      </header>

      <section className="grid gap-4 md:grid-cols-4">
        {snapshot.metrics.map((metric) => (
          <Metric key={metric.title} title={metric.title} value={metric.value} />
        ))}
      </section>

      <section className="grid gap-4 xl:grid-cols-[1.3fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Tenant Throughput</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {snapshot.tenantRows.map((row) => (
              <div
                key={row.tenant}
                className="grid gap-3 rounded-md border border-border p-3 md:grid-cols-[1fr_repeat(4,auto)]"
              >
                <div>
                  <div className="font-medium">{row.tenant}</div>
                  <Badge variant={row.status === "shadow_mode" ? "warn" : "good"}>
                    {row.status.replaceAll("_", " ")}
                  </Badge>
                </div>
                <Mini label="Throughput" value={String(row.throughput)} />
                <Mini label="Retries" value={String(row.retries)} />
                <Mini label="Dead letters" value={String(row.deadLetters)} />
                <Mini label="Approvals" value={String(row.backlog)} />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Provider Health</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {snapshot.providerHealth.map((provider) => (
              <div key={provider.provider} className="grid gap-3 rounded-md border border-border p-3 md:grid-cols-[1fr_auto_auto]">
                <div>
                  <div className="font-medium">{provider.provider}</div>
                  <div className="text-xs text-muted-foreground">Latency {provider.latency}</div>
                </div>
                <Mini label="Stale signals" value={String(provider.staleSignals)} />
                <Badge variant={provider.status === "healthy" ? "good" : "warn"}>{provider.status}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-4 xl:grid-cols-[1fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Open Incidents</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {snapshot.incidents.map((incident) => (
              <Incident
                key={incident.title}
                icon={incident.severity === "critical" ? <AlertTriangle size={16} /> : <ShieldAlert size={16} />}
                title={incident.title}
                severity={incident.severity}
              />
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tenant And Agent Controls</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-3 rounded-md border border-border p-3">
              <div>
                <div className="font-medium">Tenant state</div>
                <div className="text-xs text-muted-foreground">{controlStatus}</div>
              </div>
              <div className="flex gap-2">
                <Button
                  className="h-8"
                  variant="secondary"
                  disabled={pendingControl !== null}
                  onClick={() => runControl("pause", onPause)}
                >
                  <PauseCircle size={14} />
                  Pause
                </Button>
                <Button
                  className="h-8"
                  disabled={pendingControl !== null}
                  onClick={() => runControl("resume", onResume)}
                >
                  <PlayCircle size={14} />
                  Resume
                </Button>
              </div>
            </div>
            {agents.length === 0 ? (
              <div className="rounded-md border border-dashed border-border p-3 text-muted-foreground">
                No Headless agent identities registered.
              </div>
            ) : (
              agents.map((agent) => (
                <div key={agent.agentId} className="grid gap-3 rounded-md border border-border p-3 md:grid-cols-[1fr_auto]">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{agent.name}</span>
                      <Badge variant={agent.status === "active" ? "good" : "bad"}>{agent.status}</Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">{agent.scopes.slice(0, 4).join(", ")}</div>
                  </div>
                  <Button
                    className="h-8"
                    variant="secondary"
                    disabled={agent.status === "revoked" || pendingControl !== null}
                    onClick={() => runControl(`revoke:${agent.agentId}`, () => onRevokeAgent(agent.agentId))}
                  >
                    Revoke
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </section>
    </main>
  );
}

function Metric({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardContent>
        <div className="text-sm text-muted-foreground">{title}</div>
        <div className="mt-2 text-2xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-right text-sm">
      <div className="font-semibold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function Incident({
  icon,
  title,
  severity,
}: {
  icon: ReactNode;
  title: string;
  severity: "warn" | "critical";
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-border p-3">
      <div className="flex items-center gap-2 text-sm">
        {icon}
        <span>{title}</span>
      </div>
      <Badge variant={severity === "critical" ? "bad" : "warn"}>{severity}</Badge>
    </div>
  );
}
